import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const TransportIntroPage: React.FC = () => {
  const { language } = useLanguage();
  const t = language === 'zh-TW' ? zhTW : enUS;

  const transportServices = [
    {
      icon: '🚄',
      title: t.services.hsr.title,
      description: t.services.hsr.description,
      features: t.services.hsr.features,
      price: t.services.hsr.price,
      duration: t.services.hsr.duration
    },
    {
      icon: '🚂',
      title: t.services.train.title,
      description: t.services.train.description,
      features: t.services.train.features,
      price: t.services.train.price,
      duration: t.services.train.duration
    },
    {
      icon: '✈️',
      title: t.services.plane.title,
      description: t.services.plane.description,
      features: t.services.plane.features,
      price: t.services.plane.price,
      duration: t.services.plane.duration
    },
    {
      icon: '🚗',
      title: t.services.rental.title,
      description: t.services.rental.description,
      features: t.services.rental.features,
      price: t.services.rental.price,
      duration: t.services.rental.duration
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* 頁面標題 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t.subtitle}
          </p>
          <Link
            to="/start"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
          >
            🚀 {t.cta}
          </Link>
        </div>

        {/* 服務特色 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center">
            <div className="text-4xl mb-4">⚡</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.speed.title}</h3>
            <p className="text-gray-600">{t.features.speed.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.price.title}</h3>
            <p className="text-gray-600">{t.features.price.description}</p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🛡️</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.features.safety.title}</h3>
            <p className="text-gray-600">{t.features.safety.description}</p>
          </div>
        </div>

        {/* 交通服務列表 */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
            {t.services.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {transportServices.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-soft p-8 hover:shadow-medium transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">⏱️</span>
                    {service.duration}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">💰</span>
                    {service.price}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, featureIndex) => (
                    <span
                      key={featureIndex}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 預約流程 */}
        <div className="bg-white rounded-2xl shadow-soft p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">
            {t.process.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {t.process.steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 行動按鈕 */}
        <div className="text-center">
          <Link
            to="/start"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
          >
            🎯 {t.startBooking}
          </Link>
        </div>
      </div>
    </div>
  );
};

// 中文翻譯
const zhTW = {
  title: '交通與接駁服務',
  subtitle: '從台北到花蓮，從機場到住宿，我們提供完整的交通接駁服務，讓您的旅程更加便利舒適',
  cta: '開始預約交通',
  startBooking: '立即開始預約',
  features: {
    speed: {
      title: '快速便捷',
      description: '多種交通方式選擇，最短時間抵達目的地'
    },
    price: {
      title: '價格透明',
      description: '明碼標價，無隱藏費用，性價比最高'
    },
    safety: {
      title: '安全可靠',
      description: '專業司機，定期保養，確保旅途安全'
    }
  },
  services: {
    title: '我們的服務',
    hsr: {
      title: '高鐵 + 接駁',
      description: '搭乘高鐵到花蓮站，我們提供專車接駁到您的住宿地點',
      features: ['專車接駁', '準時服務', '行李協助'],
      price: 'NT$ 1,200起',
      duration: '約2.5小時'
    },
    train: {
      title: '台鐵列車',
      description: '搭乘台鐵列車，享受沿途美麗的海岸線風景',
      features: ['風景優美', '經濟實惠', '班次密集'],
      price: 'NT$ 400起',
      duration: '約3-4小時'
    },
    plane: {
      title: '飛機航班',
      description: '搭乘國內航班，快速抵達花蓮機場',
      features: ['快速抵達', '機場接駁', '行李託運'],
      price: 'NT$ 2,500起',
      duration: '約1小時'
    },
    rental: {
      title: '租車服務',
      description: '在花蓮租車，自由探索美麗的東海岸',
      features: ['自由行程', '多種車型', '保險包含'],
      price: 'NT$ 800/天起',
      duration: '彈性租期'
    }
  },
  process: {
    title: '預約流程',
    steps: [
      {
        title: '選擇交通方式',
        description: '根據您的需求和預算選擇最適合的交通方式'
      },
      {
        title: '填寫預約資訊',
        description: '提供出發時間、人數等詳細資訊'
      },
      {
        title: '確認預約',
        description: '我們會立即確認您的預約並發送確認信'
      },
      {
        title: '享受旅程',
        description: '準時出發，安全舒適地抵達目的地'
      }
    ]
  }
};

// 英文翻譯
const enUS = {
  title: 'Transport & Shuttle Services',
  subtitle: 'From Taipei to Hualien, from airport to accommodation, we provide complete transportation services for a convenient and comfortable journey',
  cta: 'Book Transportation',
  startBooking: 'Start Booking Now',
  features: {
    speed: {
      title: 'Fast & Convenient',
      description: 'Multiple transportation options to reach your destination quickly'
    },
    price: {
      title: 'Transparent Pricing',
      description: 'Clear pricing with no hidden fees, best value for money'
    },
    safety: {
      title: 'Safe & Reliable',
      description: 'Professional drivers, regular maintenance, ensuring travel safety'
    }
  },
  services: {
    title: 'Our Services',
    hsr: {
      title: 'HSR + Shuttle',
      description: 'Take HSR to Hualien Station, we provide shuttle service to your accommodation',
      features: ['Dedicated Shuttle', 'On-time Service', 'Luggage Assistance'],
      price: 'NT$ 1,200+',
      duration: '~2.5 hours'
    },
    train: {
      title: 'TRA Train',
      description: 'Take TRA train and enjoy the beautiful coastal scenery along the way',
      features: ['Beautiful Scenery', 'Economical', 'Frequent Service'],
      price: 'NT$ 400+',
      duration: '~3-4 hours'
    },
    plane: {
      title: 'Flight',
      description: 'Take domestic flight to quickly reach Hualien Airport',
      features: ['Fast Arrival', 'Airport Shuttle', 'Baggage Check-in'],
      price: 'NT$ 2,500+',
      duration: '~1 hour'
    },
    rental: {
      title: 'Car Rental',
      description: 'Rent a car in Hualien to freely explore the beautiful east coast',
      features: ['Flexible Itinerary', 'Multiple Models', 'Insurance Included'],
      price: 'NT$ 800/day+',
      duration: 'Flexible Duration'
    }
  },
  process: {
    title: 'Booking Process',
    steps: [
      {
        title: 'Choose Transport',
        description: 'Select the most suitable transportation based on your needs and budget'
      },
      {
        title: 'Fill Booking Info',
        description: 'Provide departure time, number of passengers and other details'
      },
      {
        title: 'Confirm Booking',
        description: 'We will immediately confirm your booking and send confirmation'
      },
      {
        title: 'Enjoy Journey',
        description: 'Depart on time and arrive safely and comfortably'
      }
    ]
  }
};

export default TransportIntroPage; 